import React, { useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { IconButton } from 'src/components/atoms';
import {
  getAvailableCaseLeadsExcludingReporters,
  getCaseLeadForDisplay,
} from 'src/components/organisms/Case/CaseTeamPanel/CaseTeamPanel.vm';
import { ReassignCaseModal } from 'src/components/organismsLegacy/CaseScreenHeader/components';
import {
  CasesQuery,
  Maybe,
  UserType,
  useReassignCaseLeadMutation,
} from 'src/graphql';
import { CaseLead, CaseLeadUpdateLocation, Option } from 'src/types';
import {
  CaseLeadCasesTableCellContainerStyled,
  CaseLeadLabel,
} from './CaseLeadCasesTableCell.styles';
import { CaseLeadsDropdown } from './CaseLeadsDropdown';

export interface CaseLeadCasesTableCellProps {
  caseId: string;
  caseLead: CaseLead;
  canAdministerCase: boolean;
  canAdministerReport: boolean;
  availableCaseLeads?: Maybe<Partial<UserType>[]> | null;
  refetch?: () => Promise<QueryObserverResult<CasesQuery, unknown>>;
  isDraft?: boolean;
  reporterIds?: number[];
}

export const CaseLeadCasesTableCell = ({
  caseId,
  caseLead,
  availableCaseLeads,
  canAdministerCase,
  canAdministerReport,
  refetch,
  isDraft,
  reporterIds,
}: CaseLeadCasesTableCellProps) => {
  const [updatedCaseManager, setUpdatedCaseManager] = useState<
    number | undefined
  >();

  const [currCaseLead, setCurrCaseLead] = useState<Option>(
    getCaseLeadForDisplay(caseLead),
  );

  const [showCaseLeadsDropdown, setShowCaseLeadsDropdown] =
    useState<boolean>(false);

  const { mutateAsync: mutateReassignCaseLead } = useReassignCaseLeadMutation();

  const handleReassignCaseLead = async (newCaseLead?: Option) => {
    if (!newCaseLead) {
      return;
    }
    await mutateReassignCaseLead({
      caseId,
      newCaseLeadId: Number(newCaseLead.value),
      location: CaseLeadUpdateLocation.CASES_TABLE,
    });
    refetch?.();

    setUpdatedCaseManager(undefined);
  };

  const isAssignCaseLeadEnabled =
    canAdministerCase && canAdministerReport && !isDraft;

  const renderDropdownOrCaseLead = () => {
    return showCaseLeadsDropdown ? (
      <CaseLeadsDropdown
        data-testid={`reassign-case-lead-dropdown-${caseId}`}
        currCaseLead={currCaseLead}
        availableCaseLeads={getAvailableCaseLeadsExcludingReporters(
          availableCaseLeads,
          reporterIds,
        )}
        onChange={(caseManager: Option) => {
          setCurrCaseLead(caseManager);
          setUpdatedCaseManager(Number(caseManager.value));
          setShowCaseLeadsDropdown(false);
        }}
      />
    ) : (
      <>
        <CaseLeadLabel>{currCaseLead.label}</CaseLeadLabel>
        {isAssignCaseLeadEnabled ? (
          <IconButton
            data-testid={`case-lead-cases-table-cell-button-${caseId}`}
            data-intercom-target="case-lead-cases-table-cell-button"
            variant="tertiary"
            size="small"
            iconName="uil-pen"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setShowCaseLeadsDropdown(true);
            }}
          />
        ) : null}
      </>
    );
  };

  return (
    <CaseLeadCasesTableCellContainerStyled
      data-testid="case-lead-cases-table-cell"
      onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
        e.stopPropagation();
      }}
    >
      {renderDropdownOrCaseLead()}
      {isAssignCaseLeadEnabled && (
        <ReassignCaseModal
          setOpen={() => setUpdatedCaseManager(undefined)}
          open={updatedCaseManager !== undefined}
          onConfirm={async () => {
            handleReassignCaseLead(currCaseLead);
          }}
          onCancel={() => {
            setCurrCaseLead(getCaseLeadForDisplay(caseLead));
            setUpdatedCaseManager(undefined);
          }}
        />
      )}
    </CaseLeadCasesTableCellContainerStyled>
  );
};
